import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useMemo } from 'react';

import {
  usePortraitBackground,
  usePresetColors,
  useRecordingAnalytics,
} from '../../../hooks';
import { selectSelectedClip, selectVideoBackground } from '../../../selector';
import {
  PrimaryColors,
  SecondaryColors,
  TertiaryColors,
} from '../../../theme/constants';
import { ColorPicker } from '../../color-presets/color-picker';

type VideoBackgroundControlsProps = {
  disabled?: boolean;
};

export const VideoBackgroundControls = ({
  disabled = false,
}: VideoBackgroundControlsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { blur } = useAppSelector(selectVideoBackground);
  const selectedClip = useAppSelector(selectSelectedClip);
  const { currentPreset } = usePresetColors();

  // Use the same color source as VideoPlayer - this ensures ColorPicker shows exactly what VideoPlayer displays
  const { portraitBgColor } = usePortraitBackground();

  // Extract theme colors from preset config for the color picker
  const themeColors = useMemo(() => {
    const config = currentPreset?.config || {};
    const allColorKeys = [
      ...PrimaryColors,
      ...SecondaryColors,
      ...TertiaryColors,
    ];
    return allColorKeys
      .map(key => config[key] as string)
      .filter(
        (color): color is string =>
          typeof color === 'string' && color.startsWith('#'),
      );
  }, [currentPreset?.config]);

  // Note: Initial color and preset change sync is now handled in the reducer
  // when '[recording] colour preset changed' is dispatched

  const handleColorChange = useCallback(
    (newColor: string) => {
      // Only dispatch if a clip is selected
      if (!selectedClip) return;
      // When color picker is used, automatically select color option (set blur to false)
      dispatch({
        type: '[recording] video background color changed',
        payload: {
          color: newColor,
          blur: false,
        },
      });
    },
    [dispatch, selectedClip],
  );

  const handleBlurToggle = useCallback(
    (blur: boolean) => {
      // Only dispatch if a clip is selected
      if (!selectedClip) return;
      dispatch({
        type: '[recording] video background blur changed',
        payload: { blur },
      });
      trackRecordingEvent('ch_creator_lab_appearance_tab_background_selected', {
        background: blur ? 'blur' : 'color',
      });
    },
    [dispatch, selectedClip, trackRecordingEvent],
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[14px] font-medium text-white leading-[20px] tracking-[0px] m-0">
        {t('content-recording.appearance.video-background.label')}
      </p>
      <div className="flex gap-[12px]">
        {/* Blur Control */}
        <button
          type="button"
          onClick={() => handleBlurToggle(true)}
          disabled={disabled}
          className={cx(
            'flex flex-1 flex-col items-center rounded-[8px] px-[6px] pt-2 pb-1 transition-colors',
            {
              'bg-spring-neutral-b5': blur,
            },
          )}
        >
          <div className="h-[32px] w-[25px] rounded-[8px] overflow-hidden">
            <div className="h-full w-full rounded-[8px] bg-blur-gradient blur-[8px]" />
          </div>
          <div className="flex h-[26px] w-full flex-col items-center justify-center">
            <p className="text-[14px] font-semibold leading-[21px] text-white m-0">
              {t('content-recording.appearance.video-background.blur')}
            </p>
          </div>
        </button>

        {/* Color Control */}
        <button
          type="button"
          onClick={() => handleBlurToggle(false)}
          disabled={disabled}
          className={cx(
            'flex flex-1 flex-col items-center rounded-[8px] px-[6px] pt-2 pb-1 transition-colors',
            {
              'bg-spring-neutral-b5': !blur,
            },
          )}
        >
          <div className="relative h-[32px] w-[25px] rounded-[8px] overflow-visible">
            <ColorPicker
              value={portraitBgColor}
              onChange={handleColorChange}
              title={t('content-recording.appearance.video-background.color')}
              shape="rectangle"
              themeColors={themeColors}
              disabled={disabled}
            />
          </div>
          <div className="flex h-[26px] w-full flex-col items-center justify-center">
            <p className="text-[14px] font-semibold leading-[21px] text-white m-0">
              {t('content-recording.appearance.video-background.color')}
            </p>
          </div>
        </button>
      </div>
    </div>
  );
};
